import api from './api';

// Types
export interface IQuestionnaireStep {
    id: string;
    section: string;
    title: string;
    description?: string;
    fields: IQuestionnaireField[];
}

export interface IQuestionnaireField {
    id: string;
    label: string;
    type: 'text' | 'textarea' | 'number' | 'dropdown' | 'select' | 'checkbox' | 'date' | 'display' | 'file' | 'email';
    required: boolean;
    options?: string[];
    placeholder?: string;
    aiHelper?: string;
    tooltip?: string;
    questionAppearance?: string;
    financialCategory?: 'asset' | 'liability' | 'none';
}

export interface IQuestionnaire {
    _id: string;
    title: string;
    description?: string;
    lawyerId: string;
    lawyerEmail: string;
    steps: IQuestionnaireStep[];
    isDefault: boolean;
    isSystemDefault?: boolean;
    isActive: boolean;
    version: number;
    assignmentCounts?: {
        total: number;
        pending: number;
        'in-progress': number;
        completed: number;
    };
    createdAt: string;
    updatedAt: string;
}

export interface IUserQuestionnaireAssignment {
    _id: string;
    questionnaireId: IQuestionnaire | string;
    userId: string;
    userEmail: string;
    lawyerId: string;
    lawyerEmail: string;
    status: 'pending' | 'in-progress' | 'completed';
    responses: Record<string, any>;
    assignedAt: string;
    startedAt?: string;
    completedAt?: string;
    notificationSentAt?: string;
    createdAt: string;
    updatedAt: string;
}

export interface ICheckPendingResponse {
    hasPending: boolean;
    pendingCount: number;
    firstPendingAssignment: IUserQuestionnaireAssignment | null;
}

export interface IGeneratedQuestionnaireDraft {
    title: string;
    description?: string;
    steps: IQuestionnaireStep[];
}

// Lawyer endpoints
export const questionnaireService = {
    // Generate questionnaire from uploaded file (PDF, CSV, JSON)
    async generateFromFile(file: File, instruction?: string): Promise<IGeneratedQuestionnaireDraft> {
        const formData = new FormData();
        formData.append('file', file);
        if (instruction) {
            formData.append('instruction', instruction);
        }
        const response = await api.post<IGeneratedQuestionnaireDraft>('/questionnaires/generate-from-file', formData);
        return response.data;
    },

    // Generate welcome questionnaire definition from uploaded file (Admin)
    async generateWelcomeQuestionnaireFromFile(file: File, instruction?: string): Promise<IGeneratedQuestionnaireDraft> {
        const formData = new FormData();
        formData.append('file', file);
        if (instruction) {
            formData.append('instruction', instruction);
        }
        const response = await api.post<IGeneratedQuestionnaireDraft>('/welcome-questionnaire/generate-from-file', formData);
        return response.data;
    },

    // Get all questionnaires for current lawyer
    async getQuestionnaires(): Promise<IQuestionnaire[]> {
        const response = await api.get<IQuestionnaire[]>('/questionnaires');
        return response.data;
    },

    // Get a single questionnaire
    async getQuestionnaireById(id: string): Promise<IQuestionnaire> {
        const response = await api.get<IQuestionnaire>(`/questionnaires/${id}`);
        return response.data;
    },

    // Create a new questionnaire
    async createQuestionnaire(data: {
        title: string;
        description?: string;
        steps: IQuestionnaireStep[];
        isDefault?: boolean;
        isActive?: boolean;
    }): Promise<IQuestionnaire> {
        const response = await api.post<IQuestionnaire>('/questionnaires', data);
        return response.data;
    },

    // Update a questionnaire
    async updateQuestionnaire(id: string, data: {
        title?: string;
        description?: string;
        steps?: IQuestionnaireStep[];
        isDefault?: boolean;
        isActive?: boolean;
    }): Promise<IQuestionnaire> {
        const response = await api.put<IQuestionnaire>(`/questionnaires/${id}`, data);
        return response.data;
    },

    // Set a questionnaire as active or inactive for the lawyer
    async setActiveQuestionnaire(id: string, isActive = true): Promise<IQuestionnaire> {
        const response = await api.post<IQuestionnaire>(`/questionnaires/${id}/set-active`, { isActive });
        return response.data;
    },

    // Delete a questionnaire
    async deleteQuestionnaire(id: string): Promise<{ message: string; deleted?: boolean; deactivated?: boolean }> {
        const response = await api.delete(`/questionnaires/${id}`);
        return response.data;
    },

    // Duplicate a questionnaire
    async duplicateQuestionnaire(id: string): Promise<IQuestionnaire> {
        const response = await api.post<IQuestionnaire>(`/questionnaires/${id}/duplicate`);
        return response.data;
    },

    // Get assignments for a questionnaire
    async getQuestionnaireAssignments(id: string): Promise<IUserQuestionnaireAssignment[]> {
        const response = await api.get<IUserQuestionnaireAssignment[]>(`/questionnaires/${id}/assignments`);
        return response.data;
    },

    // Assign questionnaire to users
    async assignQuestionnaireToUsers(id: string, userIds: string[], sendNotification = true): Promise<{
        success: boolean;
        assigned: number;
        errors?: Array<{ userId: string; email: string; error: string }>;
    }> {
        const response = await api.post(`/questionnaires/${id}/assign`, { userIds, sendNotification });
        return response.data;
    },

    // Remove assignment
    async removeAssignment(assignmentId: string): Promise<{ message: string }> {
        const response = await api.delete(`/questionnaires/assignments/${assignmentId}`);
        return response.data;
    }
};

// User endpoints
export const userQuestionnaireService = {
    // Check if user has pending questionnaires
    async checkPending(): Promise<ICheckPendingResponse> {
        const response = await api.get<ICheckPendingResponse>('/questionnaires/user/check-pending');
        return response.data;
    },

    // Get pending questionnaires
    async getPendingQuestionnaires(): Promise<IUserQuestionnaireAssignment[]> {
        const response = await api.get<IUserQuestionnaireAssignment[]>('/questionnaires/user/pending');
        return response.data;
    },

    // Get all questionnaire assignments
    async getMyAssignments(): Promise<IUserQuestionnaireAssignment[]> {
        const response = await api.get<IUserQuestionnaireAssignment[]>('/questionnaires/user/assignments');
        return response.data;
    },

    // Get a specific assignment for answering
    async getAssignment(assignmentId: string): Promise<IUserQuestionnaireAssignment> {
        const response = await api.get<IUserQuestionnaireAssignment>(`/questionnaires/user/assignments/${assignmentId}`);
        return response.data;
    },

    // Save questionnaire responses
    async saveResponses(assignmentId: string, responses: Record<string, any>, isComplete = false): Promise<IUserQuestionnaireAssignment> {
        const response = await api.post<IUserQuestionnaireAssignment>(
            `/questionnaires/user/assignments/${assignmentId}/responses`,
            { responses, isComplete }
        );
        return response.data;
    }
};
